import * as React from 'react'
import { useLocalStorage } from 'utils/hooks'
import { sliceIfMore } from 'utils/convertPlaces'
import { AirportType, PlaceSelectList } from 'utils/types'
import { ReactComponent as ReloadIcon } from 'assets/images/ReloadIcon.svg'
import { ReactComponent as CityIcon } from 'assets/images/CityIcon.svg'
import { ReactComponent as XSAirportIcon } from 'assets/images/XSAirportIcon.svg'
import { ReactComponent as SMAirportIcon } from 'assets/images/SMAirportIcon.svg'
import { DropDownContainer, List, ListSubHeader, ListItem, ListIcon } from './Styles'

const RECENT_AIRPORTS_COUNT = 3

interface Props {
  places: PlaceSelectList[] | null
  show: boolean
  inputValue: string
  localStorageKey: string
  navigationRef: React.Ref<Record<string, (e: React.SyntheticEvent) => void>>
  handleClick: (value: PlaceSelectList) => void
}

const AirportList = ({
  places,
  show,
  inputValue,
  localStorageKey,
  navigationRef,
  handleClick,
}: Props) => {
  const [selected, setSelected] = React.useState<PlaceSelectList | null>(null)
  const [recentData, setRecentData] = useLocalStorage<PlaceSelectList[]>(localStorageKey, [])
  const listRef = React.useRef<HTMLDivElement>(null!)
  const [keyPressed, setkeyPressed] = React.useState<boolean>(false)
  const selectedItemRef = React.useRef<HTMLLIElement | null>(null)
  const listScrollRef = React.useRef<number | null>(null)

  const updateRecentData = React.useCallback(
    (airport: PlaceSelectList): void => {
      const newRecentData = recentData.filter(({ id }) => id !== airport.id)
      newRecentData.unshift(airport)
      setRecentData(sliceIfMore(newRecentData, RECENT_AIRPORTS_COUNT))
    },
    [recentData, setRecentData]
  )

  const handleClickItem = React.useCallback(
    (airport: PlaceSelectList) => {
      handleClick(airport)
      updateRecentData(airport)
      setSelected(null)
    },
    [handleClick, updateRecentData]
  )

  const handleMouseEnterItem = React.useCallback((item: PlaceSelectList) => setSelected(item), [])
  const handleMouseLeaveItem = React.useCallback(() => setSelected(null), [])

  // keyboard interaction
  const getSelectedIndex = React.useCallback(
    (data: PlaceSelectList[]) => data.findIndex(({ id }) => id === selected?.id),
    [selected]
  )

  const keyCodeCallbacks = {
    Enter: (e: React.SyntheticEvent) => {
      e.preventDefault()
      if (selected) {
        handleClickItem(selected)
        setSelected(null)
        return
      }
      const data = inputValue.length ? places : recentData
      data?.length && handleClickItem(data[0])
    },
    ArrowDown: (e: React.SyntheticEvent) => {
      e.preventDefault()
      const data = inputValue.length ? places : recentData
      if (!data || !data.length) return
      const index = getSelectedIndex(data)
      if (!~index) {
        setSelected(data[0])
        return
      }
      const nextIndex = Math.min(data.length - 1, index + 1)
      setSelected(data[nextIndex])
      setkeyPressed(true)
    },
    ArrowUp: (e: React.SyntheticEvent) => {
      e.preventDefault()
      const data = inputValue.length ? places : recentData
      if (!data || !data.length) return
      const index = getSelectedIndex(data)
      if (!~index) {
        setSelected(data[0])
        return
      }
      const prevIndex = Math.max(0, index - 1)
      setSelected(data[prevIndex])
      setkeyPressed(true)
    },
  }

  React.useEffect(() => {
    if (keyPressed && selectedItemRef.current) {
      const containerHeight = parseInt(
        getComputedStyle(listRef.current.parentElement!).getPropertyValue('height'),
        10
      )
      const listScrollTop = listRef.current.scrollTop
      if (!listScrollRef.current) listScrollRef.current = containerHeight + listScrollTop

      // change logic to not to include input height and borders (in this case is 40) !
      const selectedItemBottom = selectedItemRef.current.getBoundingClientRect().bottom - 40
      const offsetPositive =
        selectedItemBottom > listScrollRef.current ? selectedItemBottom - listScrollRef.current : 0
      // change logic to not to include input height and borders (in this case is 40) !
      const selectedItemTop = selectedItemRef.current.getBoundingClientRect().top - 40
      const selectedItemHeight = selectedItemRef.current.getBoundingClientRect().height

      // change logic to use calculated values instead of check if there is offset > -15 !
      const offsetNegative = selectedItemTop < 0 ? selectedItemHeight : 0

      listRef.current.scrollTop = listScrollTop + offsetPositive - offsetNegative
      setkeyPressed(false)
    }
  }, [keyPressed])

  React.useImperativeHandle(navigationRef, () => ({
    Enter: keyCodeCallbacks.Enter,
    ArrowDown: keyCodeCallbacks.ArrowDown,
    ArrowUp: keyCodeCallbacks.ArrowUp,
  }))

  const renderItems = (item: PlaceSelectList) => {
    const { fullName, id, hasCityWithin } = item
    return (
      <ListItem
        key={id}
        selected={id === (selected?.id || null)}
        isSubItem={!inputValue.length ? false : hasCityWithin}
        onClick={() => handleClickItem(item)}
        onMouseEnter={() => handleMouseEnterItem(item)}
        onMouseLeave={handleMouseLeaveItem}
        ref={id === selected?.id ? selectedItemRef : null}
      >
        <ListIcon>{!inputValue.length ? <ReloadIcon /> : selectAirportIcon(item)}</ListIcon>
        <p>{fullName}</p>
      </ListItem>
    )
  }

  // render
  if (!show) return null

  const data = inputValue.length ? places : recentData

  if (data?.length) {
    return (
      <DropDownContainer ref={listRef}>
        {recentData.length && !inputValue.length ? (
          <ListSubHeader>Previously searched for</ListSubHeader>
        ) : null}
        <List>{data.map(renderItems)}</List>
      </DropDownContainer>
    )
  }

  return null
}

function selectAirportIcon(airport: PlaceSelectList) {
  if (airport.type === AirportType.city) return <CityIcon />
  if (airport.rating < 2) return <SMAirportIcon />
  if (airport.rating === 2) return <XSAirportIcon />
  if (airport.rating > 2) return <XSAirportIcon className={'highlighted-airport'} />
}

export default React.memo(AirportList)
